import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, ChevronLeft, ChevronRight, Plus, Trash2, ShoppingCart, 
  Users, User, UtensilsCrossed, Coffee, Sun, Moon, Cookie, X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate, toLocalDateString } from '../utils/dateUtils';

export const MealPlanner = () => {
  const { API, token } = useAuth();
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    return toLocalDateString(monday);
  });
  const [weekPlan, setWeekPlan] = useState({});
  const [recipes, setRecipes] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showShoppingList, setShowShoppingList] = useState(false);
  const [shoppingList, setShoppingList] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customMeal, setCustomMeal] = useState('');
  const [isChildMeal, setIsChildMeal] = useState(false);
  const [selectedChild, setSelectedChild] = useState('');

  const mealTypes = [
    { id: 'breakfast', label: 'Breakfast', icon: Coffee },
    { id: 'lunch', label: 'Lunch', icon: Sun },
    { id: 'dinner', label: 'Dinner', icon: Moon },
    { id: 'snack', label: 'Snack', icon: Cookie }
  ];

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    fetchData();
  }, [weekStart]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [weekRes, recipesRes, childrenRes] = await Promise.all([
        axios.get(`${API}/meals/week/${weekStart}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/recipes`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/meals/children`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setWeekPlan(weekRes.data.plan || {});
      setRecipes(recipesRes.data);
      setChildren(childrenRes.data.children || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load meal plan');
    } finally {
      setLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    return formatLocalDate(dateStr, 'en-US', { month: 'short', day: 'numeric' });
  };

  const getWeekDates = () => {
    const dates = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const openAddModal = (date, mealType) => {
    setSelectedSlot({ date, mealType });
    setCustomMeal('');
    setIsChildMeal(false);
    setSelectedChild('');
    setShowAddModal(true);
  };

  const addMeal = async (recipeId = null, recipeTitle = null) => {
    if (!selectedSlot) return;
    
    if (!recipeId && !customMeal.trim()) {
      toast.error('Please select a recipe or enter a custom meal');
      return;
    }

    if (isChildMeal && !selectedChild.trim()) {
      toast.error('Please enter the child\'s name');
      return;
    }

    try {
      await axios.post(`${API}/meals`, {
        date: selectedSlot.date,
        meal_type: selectedSlot.mealType,
        recipe_id: recipeId,
        recipe_title: recipeTitle,
        custom_meal: recipeId ? null : customMeal.trim(),
        is_child_specific: isChildMeal,
        child_name: isChildMeal ? selectedChild.trim() : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Meal added!');
      setShowAddModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add meal');
    }
  };

  const deleteMeal = async (mealId) => {
    try {
      await axios.delete(`${API}/meals/${mealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meal removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove meal');
    }
  };

  const generateShoppingList = async () => {
    const dates = getWeekDates();
    try {
      const response = await axios.get(`${API}/meals/shopping-list`, {
        params: { start_date: dates[0], end_date: dates[6] },
        headers: { Authorization: `Bearer ${token}` }
      });
      setShoppingList(response.data.items || []);
      setShowShoppingList(true);
    } catch (error) {
      toast.error('Failed to generate shopping list');
    }
  };

  const getMealDisplay = (meal) => {
    if (!meal) return null;
    return meal.recipe_title || meal.custom_meal || 'Unnamed meal';
  };

  const renderMealSlot = (date, mealType, familyMeal, childMeals) => {
    const MealIcon = mealTypes.find(m => m.id === mealType)?.icon || UtensilsCrossed;
    
    return (
      <div className="space-y-2">
        {/* Family meal */}
        {familyMeal ? (
          <div className="bg-white rounded-lg p-2 border border-stone-200 group">
            <div className="flex items-start justify-between gap-1">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Users className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm text-[#2D2D2D] truncate">{getMealDisplay(familyMeal)}</span>
              </div>
              <button
                onClick={() => deleteMeal(familyMeal.id)}
                className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => openAddModal(date, mealType)}
            className="w-full p-2 border-2 border-dashed border-stone-200 rounded-lg text-stone-400 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-1"
          >
            <Plus className="w-3 h-3" />
            <span className="text-xs">Family</span>
          </button>
        )}
        
        {/* Child-specific meals */}
        {Object.entries(childMeals || {}).map(([childName, meals]) => {
          const meal = mealType === 'snack' ? meals.snack?.[0] : meals[mealType];
          if (!meal) return null;
          return (
            <div key={childName} className="bg-amber-50 rounded-lg p-2 border border-amber-200 group">
              <div className="flex items-start justify-between gap-1">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <User className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <span className="text-xs text-amber-600 block">{childName}</span>
                    <span className="text-sm text-[#2D2D2D] truncate block">{getMealDisplay(meal)}</span>
                  </div>
                </div>
                <button
                  onClick={() => deleteMeal(meal.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 text-red-400 hover:text-red-600 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
        
        {/* Add child meal button if family meal exists */}
        {familyMeal && children.length > 0 && (
          <button
            onClick={() => {
              setIsChildMeal(true);
              openAddModal(date, mealType);
            }}
            className="w-full p-1 text-xs text-amber-600 hover:bg-amber-50 rounded transition-colors flex items-center justify-center gap-1"
          >
            <Plus className="w-3 h-3" />
            Child option
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="meal-planner">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <Calendar className="w-10 h-10 text-primary" strokeWidth={1.5} />
              <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">Meal Planner</h1>
            </div>
            <p className="text-[#57534E]">Plan family meals and accommodate individual dietary needs</p>
          </div>
          <Button
            onClick={generateShoppingList}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 flex items-center gap-2"
            data-testid="shopping-list-btn"
          >
            <ShoppingCart className="w-5 h-5" />
            Shopping List
          </Button>
        </div>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigateWeek(-1)} className="rounded-full">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-lg font-semibold text-[#2D2D2D]">
          Week of {formatDate(weekStart)}
        </h2>
        <Button variant="outline" onClick={() => navigateWeek(1)} className="rounded-full">
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-6 text-sm text-[#57534E]">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <span>Family meal</span>
        </div>
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-amber-600" />
          <span>Child-specific meal</span>
        </div>
      </div>

      {/* Meal Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-[#A8A29E]">Loading meal plan...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="w-20"></th>
                {getWeekDates().map((date, i) => (
                  <th key={date} className="p-2 text-center min-w-[120px]">
                    <div className="text-sm font-semibold text-[#2D2D2D]">{weekDays[i]}</div>
                    <div className="text-xs text-[#A8A29E]">{formatDate(date)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mealTypes.map((mealType) => (
                <tr key={mealType.id} className="border-t border-stone-100">
                  <td className="p-2 text-sm font-medium text-[#57534E] align-top">
                    <div className="flex items-center gap-2">
                      <mealType.icon className="w-4 h-4" />
                      <span>{mealType.label}</span>
                    </div>
                  </td>
                  {getWeekDates().map((date) => {
                    const dayPlan = weekPlan[date] || { family: {}, children: {} };
                    const familyMeal = mealType.id === 'snack' 
                      ? dayPlan.family?.snack?.[0] 
                      : dayPlan.family?.[mealType.id];
                    
                    return (
                      <td key={date} className="p-2 align-top bg-stone-50/50">
                        {renderMealSlot(date, mealType.id, familyMeal, dayPlan.children)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Meal Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Meal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Meal type toggle - always show */}
            <div className="flex items-center gap-4 p-3 bg-stone-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!isChildMeal}
                  onChange={() => setIsChildMeal(false)}
                  className="text-primary"
                  name="meal-type"
                />
                <Users className="w-4 h-4 text-primary" />
                <span>Family meal</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={isChildMeal}
                  onChange={() => setIsChildMeal(true)}
                  className="text-amber-600"
                  name="meal-type"
                />
                <User className="w-4 h-4 text-amber-600" />
                <span>Child-specific</span>
              </label>
            </div>

            {/* Child selector/input */}
            {isChildMeal && (
              <div>
                <label className="text-sm font-medium text-[#2D2D2D]">Child's name:</label>
                {children.length > 0 ? (
                  <select
                    value={selectedChild}
                    onChange={(e) => setSelectedChild(e.target.value)}
                    className="w-full mt-1 border border-stone-200 rounded-lg px-3 py-2"
                  >
                    <option value="">Select or type below...</option>
                    {children.map(child => (
                      <option key={child} value={child}>{child}</option>
                    ))}
                  </select>
                ) : null}
                <Input
                  value={selectedChild}
                  onChange={(e) => setSelectedChild(e.target.value)}
                  placeholder="Enter child's name..."
                  className="mt-2"
                />
              </div>
            )}

            {/* Custom meal input */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Custom meal:</label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={customMeal}
                  onChange={(e) => setCustomMeal(e.target.value)}
                  placeholder="Enter meal name..."
                  className="flex-1"
                />
                <Button onClick={() => addMeal()} disabled={!customMeal.trim()}>
                  Add
                </Button>
              </div>
            </div>

            {/* Recipe selection */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D]">Or choose a recipe:</label>
              <div className="mt-2 max-h-60 overflow-y-auto space-y-2">
                {recipes.map(recipe => (
                  <button
                    key={recipe.id}
                    onClick={() => addMeal(recipe.id, recipe.title)}
                    className="w-full p-3 text-left bg-white border border-stone-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors"
                  >
                    <div className="font-medium text-[#2D2D2D]">{recipe.title}</div>
                    <div className="text-xs text-[#A8A29E]">
                      {recipe.diet_type?.join(', ')} • {recipe.prep_time + recipe.cook_time} min
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shopping List Modal */}
      <Dialog open={showShoppingList} onOpenChange={setShowShoppingList}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Shopping List
            </DialogTitle>
          </DialogHeader>
          {shoppingList.length === 0 ? (
            <p className="text-[#A8A29E] text-center py-8">
              No recipes planned for this week. Add some recipes to generate a shopping list!
            </p>
          ) : (
            <div className="space-y-2">
              {shoppingList.map((item, index) => (
                <div key={index} className="flex items-start gap-3 p-2 hover:bg-stone-50 rounded-lg">
                  <input type="checkbox" className="mt-1" />
                  <div className="flex-1">
                    <div className="text-[#2D2D2D]">{item.ingredient}</div>
                    <div className="text-xs text-[#A8A29E]">
                      For: {item.recipes.join(', ')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MealPlanner;
